// =====================================================
// utils/ligas.ts
// Utilidades para Ligas - Multi-Deporte
// =====================================================

import { calcularEstadisticas, Deporte as DeporteBase, ModoZonas } from './resultados';

export type Deporte = DeporteBase;

// ---------------------------------------------------------
// Interfaz de Equipo de Liga
// ---------------------------------------------------------
export interface EquipoLiga {
  id: number;
  nombre: string;
  ids?: {
    idJugador1: number;
    idJugador2?: number;
  };
}

// ---------------------------------------------------------
// Interfaz de Partido de Liga
// ---------------------------------------------------------
export interface PartidoLiga {
  id: number;
  fecha: number; // Número de fecha/jornada
  local: EquipoLiga;
  visitante: EquipoLiga;
  sets?: Array<{ gamesLocal: number | null; gamesVisitante: number | null }>;
  resultado?: string;
  ganador?: EquipoLiga | null;
  detalles?: DetallesPartido;
}

export interface DetallesPartido {
  fecha?: string;
  hora?: string;
  lugar?: string;
  observaciones?: string;
}

// ---------------------------------------------------------
// Interfaz de Fecha/Jornada
// ---------------------------------------------------------
export interface Fecha {
  numero: number;
  partidos: PartidoLiga[];
}

// ---------------------------------------------------------
// Interfaz de Estadísticas de Equipo
// ---------------------------------------------------------
export interface EstadisticasEquipo {
  equipo: EquipoLiga;
  pj: number; // Partidos jugados
  pg: number; // Partidos ganados
  pe: number; // Partidos empatados (solo fútbol)
  pp: number; // Partidos perdidos
  setsGanados: number;
  setsPerdidos: number;
  setsDiff: number;
  gamesGanados: number; // Games (tenis) o goles (fútbol)
  gamesPerdidos: number;
  gamesDiff: number;
  puntos: number;
}

// ---------------------------------------------------------
// Interfaz de Liga
// ---------------------------------------------------------
export interface Liga {
  totalEquipos: number;
  equipos: EquipoLiga[];
  fechas: Fecha[];
  totalFechas: number;
  deporte?: Deporte;
}

// ---------------------------------------------------------
// NUEVA: Crear sets iniciales según deporte
// ---------------------------------------------------------
function crearSetsIniciales(deporte: Deporte): Array<{ gamesLocal: number | null; gamesVisitante: number | null }> {
  if (deporte === 'Futbol') {
    // Fútbol: solo 1 "set" que representa el partido
    return [{ gamesLocal: null, gamesVisitante: null }];
  } else {
    // Tenis/Padel: 2 sets por defecto
    return [
      { gamesLocal: null, gamesVisitante: null },
      { gamesLocal: null, gamesVisitante: null },
    ];
  }
}

// ---------------------------------------------------------
// Algoritmo Round-Robin optimizado
// ---------------------------------------------------------
function generarPartidosRoundRobin(
  equipos: EquipoLiga[],
  deporte: Deporte = 'Padel'
): PartidoLiga[][] {
  const n = equipos.length;
  const esImpar = n % 2 !== 0;
  
  // Crear lista de equipos para el algoritmo
  const listaEquipos = [...equipos];
  
  // Si es impar, agregar un "equipo fantasma" para el descanso
  const equipoDescanso: EquipoLiga = {
    id: -1,
    nombre: "DESCANSO"
  };
  
  if (esImpar) {
    listaEquipos.push(equipoDescanso);
  }
  
  const totalEquipos = listaEquipos.length;
  const totalFechas = totalEquipos - 1;
  const partidosPorFecha = Math.floor(totalEquipos / 2);
  
  const fechas: PartidoLiga[][] = [];
  
  // Crear el array fijo (primer equipo siempre en posición 0)
  const arrayFijo = listaEquipos.slice(0, 1);
  const arrayRotativo = listaEquipos.slice(1);
  
  for (let fecha = 0; fecha < totalFechas; fecha++) {
    const partidosFecha: PartidoLiga[] = [];
    
    let equipoDescansa: EquipoLiga | null = null;
    
    // El primer equipo juega contra el primero del array rotativo
    const primerPartido = {
      local: arrayFijo[0],
      visitante: arrayRotativo[0]
    };
    
    // Si alguno es el equipo descanso, guardar quién descansa
    if (primerPartido.visitante.id === -1) {
      equipoDescansa = primerPartido.local;
    } else if (primerPartido.local.id === -1) {
      equipoDescansa = primerPartido.visitante;
    } else {
      partidosFecha.push({
        id: 0,
        fecha: fecha + 1,
        local: primerPartido.local,
        visitante: primerPartido.visitante,
        sets: crearSetsIniciales(deporte),
        detalles: {}
      });
    }
    
    // Generar los demás partidos
    for (let i = 1; i < partidosPorFecha; i++) {
      const equipoA = arrayRotativo[i];
      const equipoB = arrayRotativo[totalEquipos - 1 - i];
      
      // Verificar si alguno es el equipo descanso
      if (equipoA.id === -1) {
        equipoDescansa = equipoB;
        continue;
      }
      if (equipoB.id === -1) {
        equipoDescansa = equipoA;
        continue;
      }
      
      // Alternar localía
      let local, visitante;
      if (fecha % 2 === 0) {
        local = equipoA;
        visitante = equipoB;
      } else {
        local = equipoB;
        visitante = equipoA;
      }
      
      partidosFecha.push({
        id: 0,
        fecha: fecha + 1,
        local: local,
        visitante: visitante,
        sets: crearSetsIniciales(deporte),
        detalles: {}
      });
    }
    
    fechas.push(partidosFecha);
    
    // Rotar el array rotativo
    const ultimo = arrayRotativo.pop();
    if (ultimo) {
      arrayRotativo.unshift(ultimo);
    }
  }
  
  return fechas;
}

// ---------------------------------------------------------
// Función para obtener quién descansa en cada fecha
// ---------------------------------------------------------
export function obtenerEquipoDescansoPorFecha(liga: Liga): Map<number, EquipoLiga> {
  const descansos = new Map<number, EquipoLiga>();
  
  if (liga.totalEquipos % 2 === 0) {
    return descansos;
  }
  
  liga.fechas.forEach(fecha => {
    const equiposEnFecha = new Set<number>();
    
    fecha.partidos.forEach(partido => {
      equiposEnFecha.add(partido.local.id);
      equiposEnFecha.add(partido.visitante.id);
    });
    
    const equipoDescansa = liga.equipos.find(
      equipo => !equiposEnFecha.has(equipo.id)
    );
    
    if (equipoDescansa) {
      descansos.set(fecha.numero, equipoDescansa);
    }
  });
  
  return descansos;
}

// ---------------------------------------------------------
// Generar Liga
// ---------------------------------------------------------
export function generarLiga(
  totalEquipos: number,
  deporte: Deporte = 'Tenis'
): Liga {
  if (totalEquipos < 2) {
    throw new Error("Una liga debe tener al menos 2 equipos");
  }
  
  if (totalEquipos > 50) {
    throw new Error("Una liga no puede tener más de 50 equipos");
  }
  
  // Generar nombres según el deporte
  const usaTerminoPareja = deporte === 'Padel' || deporte === 'Tenis';
  const termino = usaTerminoPareja ? 'Pareja' : 'Equipo';
  
  const equipos: EquipoLiga[] = Array.from({ length: totalEquipos }, (_, i) => ({
    id: i + 1,
    nombre: `${termino} ${i + 1}`,
  }));
  
  const partidosPorFecha = generarPartidosRoundRobin(equipos, deporte);
  
  let idContador = 1;
  const fechas: Fecha[] = partidosPorFecha.map((partidos, idx) => {
    const partidosConId = partidos.map(partido => ({
      ...partido,
      id: idContador++,
      fecha: idx + 1
    }));
    
    return {
      numero: idx + 1,
      partidos: partidosConId
    };
  });
  
  verificarIntegridadLiga(equipos, fechas);
  
  const totalFechas = fechas.length;
  
  return {
    totalEquipos,
    equipos,
    fechas,
    totalFechas,
    deporte
  };
}

// ---------------------------------------------------------
// Calcular tabla de posiciones
// ---------------------------------------------------------
export function calcularTablaPosiciones(liga: Liga): EstadisticasEquipo[] {
  const deporte = liga.deporte || 'Tenis';
  const estadisticas = new Map<number, EstadisticasEquipo>();
  
  // Inicializar estadísticas para cada equipo
  liga.equipos.forEach(equipo => {
    estadisticas.set(equipo.id, {
      equipo,
      pj: 0,
      pg: 0,
      pe: 0,
      pp: 0,
      setsGanados: 0,
      setsPerdidos: 0,
      setsDiff: 0,
      gamesGanados: 0,
      gamesPerdidos: 0,
      gamesDiff: 0,
      puntos: 0
    });
  });
  
  // Procesar todos los partidos
  liga.fechas.forEach(fecha => {
    fecha.partidos.forEach(partido => {
      if (!partido.sets || partido.sets.length === 0) return;
      
      const minimosRequeridos = deporte === 'Futbol' ? 1 : 2;
      if (partido.sets.length < minimosRequeridos) return;
      
      const todosLosSetsTienenResultado = partido.sets.every(
        set => 
          set.gamesLocal !== null && 
          set.gamesLocal !== undefined &&
          set.gamesVisitante !== null && 
          set.gamesVisitante !== undefined
      );
      
      if (!todosLosSetsTienenResultado) return;
      
      const statsLocal = estadisticas.get(partido.local.id)!;
      const statsVisitante = estadisticas.get(partido.visitante.id)!;
      
      // Usar la función centralizada
      const statsPartidoLocal = calcularEstadisticas(
        [partido],
        partido.local.id,
        deporte,
        'flexible' as ModoZonas
      );
      
      const statsPartidoVisitante = calcularEstadisticas(
        [partido],
        partido.visitante.id,
        deporte,
        'flexible' as ModoZonas
      );
      
      // Acumular estadísticas
      statsLocal.pj += statsPartidoLocal.pj;
      statsLocal.pg += statsPartidoLocal.pg;
      statsLocal.pe += statsPartidoLocal.pe;
      statsLocal.pp += statsPartidoLocal.pp;
      statsLocal.setsGanados += statsPartidoLocal.setsGanados;
      statsLocal.setsPerdidos += statsPartidoLocal.setsPerdidos;
      statsLocal.gamesGanados += statsPartidoLocal.gamesGanados;
      statsLocal.gamesPerdidos += statsPartidoLocal.gamesPerdidos;
      statsLocal.puntos += statsPartidoLocal.puntos;
      
      statsVisitante.pj += statsPartidoVisitante.pj;
      statsVisitante.pg += statsPartidoVisitante.pg;
      statsVisitante.pe += statsPartidoVisitante.pe;
      statsVisitante.pp += statsPartidoVisitante.pp;
      statsVisitante.setsGanados += statsPartidoVisitante.setsGanados;
      statsVisitante.setsPerdidos += statsPartidoVisitante.setsPerdidos;
      statsVisitante.gamesGanados += statsPartidoVisitante.gamesGanados;
      statsVisitante.gamesPerdidos += statsPartidoVisitante.gamesPerdidos;
      statsVisitante.puntos += statsPartidoVisitante.puntos;
      
      // Calcular diferencias
      statsLocal.setsDiff = statsLocal.setsGanados - statsLocal.setsPerdidos;
      statsLocal.gamesDiff = statsLocal.gamesGanados - statsLocal.gamesPerdidos;
      statsVisitante.setsDiff = statsVisitante.setsGanados - statsVisitante.setsPerdidos;
      statsVisitante.gamesDiff = statsVisitante.gamesGanados - statsVisitante.gamesPerdidos;
    });
  });
  
  // Convertir a array y ordenar
  const tabla = Array.from(estadisticas.values());
  
  tabla.sort((a, b) => {
    // 1. Por puntos
    if (b.puntos !== a.puntos) return b.puntos - a.puntos;
    
    if (deporte === 'Futbol') {
      // FÚTBOL
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    } else {
      // TENIS/PADEL
      if (b.setsDiff !== a.setsDiff) return b.setsDiff - a.setsDiff;
      if (b.setsGanados !== a.setsGanados) return b.setsGanados - a.setsGanados;
      if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
      if (b.gamesGanados !== a.gamesGanados) return b.gamesGanados - a.gamesGanados;
    }
    
    return a.equipo.nombre.localeCompare(b.equipo.nombre);
  });
  
  return tabla;
}

// ---------------------------------------------------------
// Función de verificación de integridad
// ---------------------------------------------------------
function verificarIntegridadLiga(equipos: EquipoLiga[], fechas: Fecha[]): void {
  const partidosPorEquipoPorFecha = new Map<number, Map<number, number>>();
  
  equipos.forEach(equipo => {
    partidosPorEquipoPorFecha.set(equipo.id, new Map<number, number>());
  });
  
  fechas.forEach(fecha => {
    const numeroFecha = fecha.numero;
    
    fecha.partidos.forEach(partido => {
      const contadorLocal = partidosPorEquipoPorFecha.get(partido.local.id)!;
      contadorLocal.set(numeroFecha, (contadorLocal.get(numeroFecha) || 0) + 1);
      
      const contadorVisitante = partidosPorEquipoPorFecha.get(partido.visitante.id)!;
      contadorVisitante.set(numeroFecha, (contadorVisitante.get(numeroFecha) || 0) + 1);
    });
  });
  
  let errores = 0;
  partidosPorEquipoPorFecha.forEach((contadorFecha, equipoId) => {
    contadorFecha.forEach((cantidad, fechaNum) => {
      if (cantidad > 1) {
        console.error(`ERROR: Equipo ${equipoId} juega ${cantidad} partidos en la fecha ${fechaNum}`);
        errores++;
      }
    });
  });
  
  const totalPartidosEsperados = equipos.length - 1;
  equipos.forEach(equipo => {
    const totalPartidosEquipo = Array.from(
      partidosPorEquipoPorFecha.get(equipo.id)!.values()
    ).reduce((sum, count) => sum + count, 0);
    
    if (totalPartidosEquipo !== totalPartidosEsperados) {
      console.warn(`ADVERTENCIA: Equipo ${equipo.id} juega ${totalPartidosEquipo} partidos, esperados ${totalPartidosEsperados}`);
    }
  });
  
  if (errores === 0) {
    console.log("✓ La liga tiene integridad correcta");
  }
}

// ---------------------------------------------------------
// Obtener partidos de un equipo específico
// ---------------------------------------------------------
export function obtenerPartidosDeEquipo(liga: Liga, equipoId: number): PartidoLiga[] {
  const partidos: PartidoLiga[] = [];
  
  liga.fechas.forEach(fecha => {
    fecha.partidos.forEach(partido => {
      if (partido.local.id === equipoId || partido.visitante.id === equipoId) {
        partidos.push(partido);
      }
    });
  });
  
  return partidos;
}

// ---------------------------------------------------------
// Obtener estadísticas de enfrentamientos directos
// ---------------------------------------------------------
export function obtenerEnfrentamientosDirectos(
  liga: Liga, 
  equipoId1: number, 
  equipoId2: number
): PartidoLiga[] {
  const partidos: PartidoLiga[] = [];
  
  liga.fechas.forEach(fecha => {
    fecha.partidos.forEach(partido => {
      const esEnfrentamiento = 
        (partido.local.id === equipoId1 && partido.visitante.id === equipoId2) ||
        (partido.local.id === equipoId2 && partido.visitante.id === equipoId1);
      
      if (esEnfrentamiento) {
        partidos.push(partido);
      }
    });
  });
  
  return partidos;
}

// ---------------------------------------------------------
// Verificar si la liga está completa
// ---------------------------------------------------------
export function estaLigaCompleta(liga: Liga): boolean {
  const deporte = liga.deporte || 'Tenis';
  const minimosRequeridos = deporte === 'Futbol' ? 1 : 2;
  
  return liga.fechas.every(fecha => 
    fecha.partidos.every(partido => 
      partido.sets && 
      partido.sets.length >= minimosRequeridos &&
      partido.sets.every(set => 
        set.gamesLocal !== null && 
        set.gamesLocal !== undefined &&
        set.gamesVisitante !== null && 
        set.gamesVisitante !== undefined
      )
    )
  );
}

// ---------------------------------------------------------
// Obtener progreso de la liga
// ---------------------------------------------------------
export function obtenerProgresoLiga(liga: Liga): {
  partidosJugados: number;
  partidosTotales: number;
  porcentaje: number;
} {
  const deporte = liga.deporte || 'Tenis';
  const minimosRequeridos = deporte === 'Futbol' ? 1 : 2;
  
  let partidosJugados = 0;
  let partidosTotales = 0;
  
  liga.fechas.forEach(fecha => {
    fecha.partidos.forEach(partido => {
      partidosTotales++;
      
      const tieneResultado = partido.sets && 
        partido.sets.length >= minimosRequeridos &&
        partido.sets.every(set => 
          set.gamesLocal !== null && 
          set.gamesLocal !== undefined &&
          set.gamesVisitante !== null && 
          set.gamesVisitante !== undefined
        );
      
      if (tieneResultado) {
        partidosJugados++;
      }
    });
  });
  
  const porcentaje = partidosTotales > 0 
    ? Math.round((partidosJugados / partidosTotales) * 100) 
    : 0;
  
  return {
    partidosJugados,
    partidosTotales,
    porcentaje
  };
}